import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import moment from "moment";
import { getDateByDayOfWeek } from "../../../../utils/constanst.js";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";
import { autocompletePartners } from "../../../../services/partner.service.ts";

function normalizeShiftRow(shift: string): string {
  return String(shift ?? "").trim();
}

function findPartnerForPositionShift(
  scheduleList: any[],
  positionId: number,
  shift: string,
  partnerList: any[],
): any | null {
  const match = scheduleList.find((s: any) => {
    const sPos = Number(s.position?.id ?? s.position);
    if (sPos !== Number(positionId)) return false;
    if (normalizeShiftRow(s.shift) !== normalizeShiftRow(shift)) return false;
    return Boolean(s.partner?.id ?? s.partner);
  });
  if (!match) return null;
  const pid = match.partner?.id ?? match.partner;
  if (match.partner?.person) return match.partner;
  return partnerList.find((p: any) => Number(p.id) === Number(pid)) || null;
}

interface ConfigHorarioSemanalRowProps {
  position: any;
  assignedPartner: any;
  matchedPartner: any;
  weekDays: string[];
  weekValue: string;
  schedules: any[];
  partners: any[];
  shift: string;
  shiftBgColor: string;
  availableRoles: string[];
  editingCell: { positionId: string; day: string } | null;
  setEditingCell: (cell: { positionId: string; day: string } | null) => void;
  rowIndex: number;
  branchId: number;
  freeDays: any[];
  scheduleViewMode?: boolean;
  getScheduleItemForCell?: (positionId: number, shift: string, day: string) => any;
  getScheduleItemsForCell?: (positionId: number, shift: string, day: string) => any[];
  onDayClick?: (day: string, positionName?: string, positionShift?: string) => void;
}

const ConfigHorarioSemanalRow = forwardRef<{ getRowData: () => any; fillWeekWithMatching: (partner: any, position: any) => boolean; syncDayWithSchedule: (day: string, schedule: any) => boolean }, ConfigHorarioSemanalRowProps>(({
  position,
  assignedPartner,
  matchedPartner,
  weekDays,
  weekValue,
  schedules,
  partners,
  shift,
  shiftBgColor,
  availableRoles,
  editingCell,
  setEditingCell,
  rowIndex,
  branchId,
  freeDays = [],
  scheduleViewMode = false,
  getScheduleItemForCell,
  getScheduleItemsForCell,
  onDayClick,
}, ref) => {
  const rolesList = [...availableRoles, "N/A", "DESCANSO"];

  // Obtener rol por defecto para una posición
  const getDefaultRoleForPosition = React.useCallback(
    (positionName: string): string => {
      const exactMatch = availableRoles.find((role) => role === positionName);
      if (exactMatch) {
        return exactMatch;
      }

      const positionLower = positionName.toLowerCase();
      const partialMatch = availableRoles.find(
        (role) =>
          role.toLowerCase().includes(positionLower) ||
          positionLower.includes(role.toLowerCase())
      );
      if (partialMatch) {
        return partialMatch;
      }

      return availableRoles.includes(positionName) ? positionName : "N/A";
    },
    [availableRoles]
  );

  // Helper functions para freeDays (deben estar antes de getInitialRoles)
  const getFreeDayForPartner = (partnerId: number): any => {
    if (!freeDays || !Array.isArray(freeDays)) return null;
    return freeDays.find((fd: any) => fd.partner?.id === partnerId);
  };

  const isFreeDay = (day: string, partnerId: number): boolean => {
    const freeDay = getFreeDayForPartner(partnerId);
    if (!freeDay || !freeDay.days) return false;
    return freeDay.days.includes(day);
  };

  const getInitialPartner = () => {
    const fromSchedule = findPartnerForPositionShift(
      schedules,
      position.positionId,
      shift,
      partners,
    );
    if (fromSchedule) return fromSchedule;
    return matchedPartner || null;
  };

  const getScheduleForPositionPartnerDay = (
    day: string,
    partnerId: number,
  ): any | null => {
    return (
      schedules.find(
        (s: any) =>
          Number(s.position?.id ?? s.position) === Number(position.positionId) &&
          normalizeShiftRow(s.shift) === normalizeShiftRow(shift) &&
          s.dayOfWeek === day &&
          Number(s.partner?.id ?? s.partner) === Number(partnerId),
      ) || null
    );
  };

  // Inicializar roles desde BranchSchedule y freeDays
  const getInitialRoles = (partner: any): { [key: string]: string } => {
        const initialRoles: { [key: string]: string } = {};
    
    if (!partner) {
        return initialRoles;
      }

    weekDays.forEach((day) => {
      const scheduleItem = getScheduleForPositionPartnerDay(day, partner.id);
      if (scheduleItem) {
        initialRoles[day] =
          scheduleItem.positionName ||
          scheduleItem.position?.name ||
          position.positionDisplayName ||
          "";
      }

      if (!initialRoles[day] && isFreeDay(day, partner.id)) {
        initialRoles[day] = "DESCANSO";
      }
    });

    return initialRoles;
  };

  const initialPartner = getInitialPartner();
  const [selectedPartner, setSelectedPartner] = useState<any>(initialPartner);
  const [selectedRoles, setSelectedRoles] = useState<{ [key: string]: string }>(
    getInitialRoles(initialPartner)
  );

  // Actualizar partner cuando cambien schedules o matchedPartner
  useEffect(() => {
    const partnerFromSchedule = getInitialPartner();
    if (partnerFromSchedule && partnerFromSchedule?.id !== selectedPartner?.id) {
      setSelectedPartner(partnerFromSchedule);
    } else if (!partnerFromSchedule && selectedPartner) {
      const hasAnyData = schedules.some(
        (s: any) =>
          Number(s.position?.id ?? s.position) === Number(position.positionId) &&
          normalizeShiftRow(s.shift) === normalizeShiftRow(shift),
      );
      if (!hasAnyData && !matchedPartner) {
        setSelectedPartner(null);
      }
    }
  }, [schedules, matchedPartner?.id]);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [searchPartner, setSearchPartner] = useState("");
  const [filteredPartners, setFilteredPartners] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [searchRole, setSearchRole] = useState<{ [key: string]: string }>({});
  const [filteredRoles, setFilteredRoles] = useState<{ [key: string]: string[] }>({});
  const roleModalRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const roleInputRef = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const roleTimeoutRef = useRef<{ [key: string]: NodeJS.Timeout | null }>({});

  const getScheduleItemForDay = (day: string): any => {
    if (!selectedPartner) return null;
    return getScheduleForPositionPartnerDay(day, selectedPartner.id);
  };

  // Verificar si hay conflicto: puesto asignado pero también tiene día libre configurado
  const hasFreeDayConflict = (day: string): boolean => {
    if (!selectedPartner) return false;
    
    const scheduleItem = getScheduleItemForDay(day);
    
    const hasFreeDay = isFreeDay(day, selectedPartner.id);
    if (!hasFreeDay) return false;
    
    if (scheduleItem && (scheduleItem.position || scheduleItem.positionName)) {
      return true;
    }
    
    const manualRole = selectedRoles[day];
    if (manualRole && manualRole !== "DESCANSO" && manualRole !== "N/A") {
      return true;
    }
    
    return false;
  };

  const getRoleForDay = (day: string): string => {
    if (!selectedPartner) {
      return "N/A";
    }

    if (selectedRoles[day] && selectedRoles[day] !== "N/A") {
      return selectedRoles[day];
    }

    if (isFreeDay(day, selectedPartner.id)) {
      return "DESCANSO";
    }

    const scheduleItem = getScheduleItemForDay(day);
    if (scheduleItem) {
      return (
        scheduleItem.positionName ||
        scheduleItem.position?.name ||
        position.positionDisplayName ||
        ""
      );
    }

    return "N/A";
  };

  // Actualizar roles cuando cambien schedules, selectedPartner o freeDays
  useEffect(() => {
    if (!selectedPartner) {
      setSelectedRoles({});
      return;
    }

    setSelectedRoles((prevRoles) => {
      const newRoles: { [key: string]: string } = { ...prevRoles };
      let hasChanges = false;

      weekDays.forEach((day) => {
        const scheduleItem = getScheduleItemForDay(day);
        const hasManualChange = prevRoles[day] && prevRoles[day] !== "N/A" && prevRoles[day] !== "DESCANSO";

        if (!hasManualChange && isFreeDay(day, selectedPartner.id)) {
          if (newRoles[day] !== "DESCANSO") {
            newRoles[day] = "DESCANSO";
            hasChanges = true;
          }
        } else if (scheduleItem && !hasManualChange) {
          const positionName =
            scheduleItem.positionName ||
            scheduleItem.position?.name ||
            position.positionDisplayName ||
            "";
          if (newRoles[day] !== positionName) {
            newRoles[day] = positionName;
            hasChanges = true;
          }
        } else if (!hasManualChange && !scheduleItem && !isFreeDay(day, selectedPartner.id)) {
          if (newRoles[day]) {
            delete newRoles[day];
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newRoles : prevRoles;
    });
  }, [selectedPartner?.id, schedules, freeDays]);

  const handleRoleChange = (day: string, role: string) => {
    setSelectedRoles((prev) => ({ ...prev, [day]: role }));
    setSearchRole((prev) => ({ ...prev, [day]: role }));
  };

  const handleRoleSearch = (day: string, searchTerm: string) => {
    setSearchRole((prev) => ({ ...prev, [day]: searchTerm }));

    if (roleTimeoutRef.current[day]) {
      clearTimeout(roleTimeoutRef.current[day]);
    }

    roleTimeoutRef.current[day] = setTimeout(() => {
      const filtered = rolesList.filter((role) =>
        role.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRoles((prev) => ({ ...prev, [day]: filtered }));
    }, 100);
  };


  const handlePartnerSearch = async (searchTerm: string) => {
    setSearchPartner(searchTerm);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setSearchLoading(true);

    timeoutRef.current = setTimeout(async () => {
      try {
        const data = await autocompletePartners(searchTerm, branchId);
        setFilteredPartners(data || []);
      } catch (error) {
        console.error("Error searching partners:", error);
        setFilteredPartners([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handlePartnerSelect = (partner: any) => {
    setSelectedPartner(partner);
    setSearchPartner(
      `${partner.person.firstName} ${partner.person.lastName}`
    );
    setFilteredPartners([]);
  };

  const handleOpenModal = () => {
    setShowPartnerModal(true);
    setSearchPartner("");
    setSearchLoading(true);
    setFilteredPartners([]);
    setTimeout(() => {
      inputRef.current?.focus();
      handlePartnerSearch("");
    }, 100);
  };

  const handleCloseModal = () => {
    setShowPartnerModal(false);
    setSearchPartner("");
    setFilteredPartners([]);
    setSearchLoading(false);
  };

  const handleOpenRoleModal = (day: string) => {
    const currentRole = getRoleForDay(day);
    setSearchRole((prev) => ({ ...prev, [day]: "" }));
    const filtered = rolesList.filter((role) =>
      role.toLowerCase().includes("".toLowerCase())
    );
    setFilteredRoles((prev) => ({ ...prev, [day]: filtered }));
    setTimeout(() => {
      roleInputRef.current[day]?.focus();
    }, 100);
  };

  const handleCloseRoleModal = (day: string) => {
    setSearchRole((prev) => {
      const newSearch = { ...prev };
      delete newSearch[day];
      return newSearch;
    });
    setFilteredRoles((prev) => {
      const newFiltered = { ...prev };
      delete newFiltered[day];
      return newFiltered;
    });
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        handleCloseModal();
      }
    };

    if (showPartnerModal) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showPartnerModal]);

  useEffect(() => {
    const handleClickOutsideRole = (event: MouseEvent) => {
      if (editingCell && editingCell.positionId === position.id) {
        const target = event.target as HTMLElement;
        const modalElement = roleModalRef.current[editingCell.day];
        const inputElement = roleInputRef.current[editingCell.day];
        
        if (
          modalElement &&
          !modalElement.contains(target) &&
          inputElement !== document.activeElement
        ) {
          handleCloseRoleModal(editingCell.day);
          setEditingCell(null);
        }
      }
    };

    if (editingCell && editingCell.positionId === position.id) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutsideRole);
      }, 100);
      return () => document.removeEventListener("mousedown", handleClickOutsideRole);
    }
  }, [editingCell]);

  useEffect(() => {
    return () => {
      Object.values(roleTimeoutRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);


  const isEditing = editingCell?.positionId === position.id;

  const isEven = rowIndex % 2 === 0;
  const rowBgColor = isEven ? "#FFFFFF" : "#F9FAFB";

  const getScheduleDisplayForDay = (day: string) => {
    if (!getScheduleItemForCell) return null;
    return getScheduleItemForCell(position.positionId, shift, day);
  };

  /** Todos los horarios para una celda (varias personas en el mismo puesto el mismo día). */
  const getScheduleDisplaysForDay = (day: string): any[] => {
    if (!getScheduleItemsForCell) return [];
    return getScheduleItemsForCell(position.positionId, shift, day);
  };

  useImperativeHandle(ref, () => ({
    getRowData: () => {
      if (!selectedPartner) {
        return null;
      }

      const items = weekDays.map((day) => {
        const role = getRoleForDay(day);
        
        // Solo incluir items que tienen datos (no N/A)
        if (role === "N/A" || !role) {
          return null;
        }

        const weekendItem = getWeekendItemForDay(day);
        const freeDay = role === "DESCANSO" ? getFreeDayForPartner(selectedPartner.id) : null;
        
        // Determinar positionDisplayName:
        // - Si es descanso: "Descanso"
        // - Si es cambio manual: usar el rol seleccionado
        // - Si viene del backend: usar positionDisplayName del item
        // - Por defecto: usar el rol actual
        const positionDisplayName = role === "DESCANSO" 
          ? "Descanso" 
          : selectedRoles[day] && selectedRoles[day] !== "N/A" && selectedRoles[day] !== "DESCANSO"
            ? selectedRoles[day] // Cambio manual: usar el rol seleccionado
            : (weekendItem?.positionDisplayName || role); // Del backend o rol por defecto
        
        return {
          positionId: role === "DESCANSO" ? null : position.positionId,
          positionName: role === "DESCANSO" ? null : position.positionName,
          positionDisplayName: positionDisplayName,
          shift: position.shift,
          day: day,
          partnerId: selectedPartner.id,
          role: role,
          freeDayId: freeDay?.id || weekendItem?.freeDay?.id || null,
        };
      }).filter((item) => item !== null);

      return { items };
    },
    fillWeekWithMatching: (partner: any, position: any) => {
      if (!partner || !position) {
        return false;
      }

      // Asignar el partner si no está asignado
      if (!selectedPartner || selectedPartner.id !== partner.id) {
        setSelectedPartner(partner);
      }

      // Obtener el rol por defecto para la posición
      const defaultRole = getDefaultRoleForPosition(position.positionName);
      
      // Si no hay rol por defecto válido, no hacer nada
      if (!defaultRole || defaultRole === "N/A") {
        return false;
      }

      // Rellenar toda la semana con el rol por defecto (excepto si tiene freeDay configurado)
      const newRoles: { [key: string]: string } = {};
      weekDays.forEach((day) => {
        // Solo asignar si no tiene día libre configurado
        if (!isFreeDay(day, partner.id)) {
          newRoles[day] = defaultRole;
        }
      });

      // Actualizar los roles seleccionados
      setSelectedRoles((prev) => ({ ...prev, ...newRoles }));
      
      return true;
    },
    syncDayWithSchedule: (day: string, schedule: any) => {
      if (!schedule || !schedule.partner) return false;

      const partner = partners.find((p: any) => p.id === schedule.partner.id);
      if (!partner) return false;

      if (isFreeDay(day, partner.id)) {
        return false;
      }

      if (!selectedPartner || selectedPartner.id !== partner.id) {
        setSelectedPartner(partner);
      }

      const schedulePositionName = schedule.positionName || schedule.position?.name || "";
      const roleMatch = availableRoles.find((role) => {
        const roleNormalized = role.toLowerCase().trim();
        const positionNormalized = schedulePositionName.toLowerCase().trim().replace(/\s+\d+$/, "");
        return roleNormalized === positionNormalized || 
               roleNormalized.includes(positionNormalized) || 
               positionNormalized.includes(roleNormalized);
      });

      const roleToAssign = roleMatch || schedulePositionName;

      if (roleToAssign && roleToAssign !== "N/A") {
        setSelectedRoles((prev) => {
          if (prev[day] !== roleToAssign) {
            return { ...prev, [day]: roleToAssign };
          }
          return prev;
        });
        return true;
      }

      return false;
    },
  }));

  if (scheduleViewMode && getScheduleItemForCell) {
    return (
      <tr style={{ backgroundColor: rowBgColor, borderBottom: "1px solid #e5e7eb" }}>
        <td
          style={{
            padding: "8px 12px",
            fontWeight: "600",
            color: "#1F2937",
            fontSize: "12px",
            backgroundColor: "#F3F4F6",
            textAlign: "center",
          }}
        >
          {position.positionDisplayName}
        </td>
        {weekDays.map((day) => {
          const items = getScheduleDisplaysForDay(day);
          return (
            <td
              key={day}
              onClick={() => onDayClick?.(day, position.positionDisplayName, position.shift)}
              title={onDayClick ? `Ver horario del ${day}` : undefined}
              style={{
                padding: "6px 8px",
                backgroundColor: rowBgColor,
                fontSize: "11px",
                textAlign: "center",
                verticalAlign: "top",
                cursor: onDayClick ? "pointer" : "default",
                transition: "background-color 0.15s",
              }}
              onMouseEnter={(e) => {
                if (onDayClick) e.currentTarget.style.backgroundColor = "#EFF6FF";
              }}
              onMouseLeave={(e) => {
                if (onDayClick) e.currentTarget.style.backgroundColor = rowBgColor;
              }}
            >
              {items.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  {items.map((item: any, idx: number) => {
                    const partner = item?.partner;
                    const startTimeRaw = String(item?.startTime ?? "").trim();
                    const endTimeRaw = String(item?.endTime ?? "").trim();
                    const formatPart = (t: string) => (t.length >= 5 ? t.slice(0, 5) : t);
                    const firstOf = (s: string) => {
                      if (!s) return "";
                      if (s.includes(" - ")) return formatPart(s.split(" - ")[0]?.trim() || s);
                      return formatPart(s);
                    };
                    const lastOf = (s: string) => {
                      if (!s) return "";
                      if (s.includes(" - ")) {
                        const parts = s.split(" - ").map((x: string) => x.trim());
                        return formatPart(parts[parts.length - 1] || parts[0] || s);
                      }
                      return formatPart(s);
                    };
                    const inicio = firstOf(startTimeRaw);
                    const fin = lastOf(endTimeRaw);
                    const timeStr = inicio && fin ? `${inicio} - ${fin}` : inicio || fin || "";
                    const partnerName = partner
                      ? toCamelCase(`${partner.person?.firstName || ""} ${partner.person?.lastName || ""} ${partner.person?.secondLastName || ""}`.trim())
                      : "";
                    return (
                      <div
                        key={idx}
                        style={{
                          lineHeight: 1.35,
                          padding: "5px 6px",
                          borderRadius: "6px",
                          backgroundColor: "rgba(107, 114, 128, 0.02)",
                          border: "1px solid rgba(107, 114, 128, 0.08)",
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#1F2937", marginBottom: "2px", fontSize: "11px" }}>
                          {partnerName || "-"}
                        </div>
                        {timeStr && (
                          <div style={{ color: "#6B7280", fontSize: "10px" }}>
                            [{timeStr}]
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                ""
              )}
            </td>
          );
        })}
      </tr>
    );
  }

  return (
    <tr
      style={{
        backgroundColor: rowBgColor,
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      <td
        style={{
          padding: "8px 12px",
          fontWeight: "600",
          color: "#1F2937",
          fontSize: "12px",
          backgroundColor: "#F3F4F6",
          textAlign: "center",
        }}
      >
        {position.positionDisplayName}
      </td>
      <td
        style={{
          padding: "6px 12px",
          backgroundColor: "#F3F4F6",
          position: "relative",
        }}
      >
        {showPartnerModal ? (
          <input
            ref={inputRef}
            type="text"
            value={searchPartner}
            onChange={(e) => handlePartnerSearch(e.target.value)}
            placeholder="Buscar colaborador..."
            autoFocus
            style={{
              width: "100%",
              padding: "4px 8px",
              fontSize: "12px",
              border: "1px solid #1E90FF",
              borderRadius: "4px",
              backgroundColor: "#fff",
              outline: "none",
              fontWeight: selectedPartner ? "700" : "400",
              color: "#1F2937",
              boxShadow: "0 0 0 2px rgba(30, 144, 255, 0.1)",
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                handleCloseModal();
              }
            }}
            onBlur={(e) => {
              setTimeout(() => {
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (
                  !modalRef.current?.contains(relatedTarget) &&
                  document.activeElement !== inputRef.current
                ) {
                  handleCloseModal();
                }
              }, 200);
            }}
          />
        ) : selectedPartner ? (
          <div
            style={{
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              color: "#1F2937",
              fontWeight: "700",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#F3F4F6";
              e.currentTarget.style.color = "#1E90FF";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#1F2937";
            }}
            onClick={handleOpenModal}
          >
            {toCamelCase(
              `${selectedPartner.person.firstName} ${selectedPartner.person.lastName}`
            )}
          </div>
        ) : (
          <div
            style={{
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "4px",
              color: "#9CA3AF",
              fontStyle: "italic",
              fontSize: "11px",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#F3F4F6";
              e.currentTarget.style.color = "#6B7280";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#9CA3AF";
            }}
            onClick={handleOpenModal}
          >
            <span>Sin asignar</span>
          </div>
        )}
        {showPartnerModal && (
          <div
            ref={modalRef}
            style={{
              position: "absolute",
              top: "100%",
              left: "0",
              marginTop: "4px",
              width: "300px",
              backgroundColor: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
              zIndex: 1000,
              maxHeight: "250px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {searchLoading && (
              <>
                <style>{`
                  @keyframes skeleton-loading {
                    0% {
                      opacity: 0.6;
                    }
                    50% {
                      opacity: 1;
                    }
                    100% {
                      opacity: 0.6;
                    }
                  }
                `}</style>
                <div>
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #F3F4F6",
                      }}
                    >
                      <div
                        style={{
                          height: "16px",
                          backgroundColor: "#E5E7EB",
                          borderRadius: "4px",
                          width: `${70 + index * 5}%`,
                          animation: "skeleton-loading 1.5s ease-in-out infinite",
                          animationDelay: `${index * 0.1}s`,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
            {!searchLoading && filteredPartners.length > 0 && (
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {(() => {
                  const sortedPartners = [...filteredPartners].sort((a, b) => {
                    if (selectedPartner && a.id === selectedPartner.id) return -1;
                    if (selectedPartner && b.id === selectedPartner.id) return 1;
                    return 0;
                  });
                  return sortedPartners.map((partner) => (
                    <div
                      key={partner.id}
                      onClick={() => {
                        handlePartnerSelect(partner);
                        handleCloseModal();
                      }}
                      style={{
                        padding: "10px 12px",
                        fontSize: "12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #F3F4F6",
                        transition: "background-color 0.2s",
                        backgroundColor: selectedPartner?.id === partner.id ? "#EFF6FF" : "transparent",
                        color: selectedPartner?.id === partner.id ? "#1E90FF" : "#1F2937",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPartner?.id !== partner.id) {
                          e.currentTarget.style.backgroundColor = "#F3F4F6";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPartner?.id !== partner.id) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      {toCamelCase(
                        `${partner.person.firstName} ${partner.person.lastName}`
                      )}
                    </div>
                  ));
                })()}
              </div>
            )}
            {!searchLoading &&
              searchPartner &&
              filteredPartners.length === 0 && (
                <div
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#9CA3AF",
                  }}
                >
                  No se encontraron colaboradores
                </div>
              )}
          
          </div>
        )}
      </td>
      {weekDays.map((day, dayIndex) => {
        const currentRole = getRoleForDay(day);
        const cellKey = `${position.id}-${day}`;
        const isCellEditing =
          editingCell?.positionId === position.id && editingCell?.day === day;

        return (
          <td
            key={dayIndex}
            style={{
              padding: "4px 6px",
              backgroundColor: rowBgColor,
              textAlign: "center",
              position: "relative",
            }}
            onClick={() => {
              if (!isCellEditing) {
                setEditingCell({ positionId: position.id, day });
                handleOpenRoleModal(day);
              }
            }}
          >
            {isCellEditing ? (
              <>
                <input
                  ref={(el) => {
                    if (el) roleInputRef.current[day] = el;
                  }}
                  type="text"
                  value={searchRole[day] ?? ""}
                  onChange={(e) => handleRoleSearch(day, e.target.value)}
                  placeholder="Buscar rol..."
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "4px 6px",
                    fontSize: "11px",
                    border: "1px solid #1E90FF",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    outline: "none",
                    textAlign: "center",
                    boxShadow: "0 0 0 2px rgba(30, 144, 255, 0.1)",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      handleCloseRoleModal(day);
                      setEditingCell(null);
                    }
                  }}
                  onBlur={(e) => {
                    setTimeout(() => {
                      const modalElement = roleModalRef.current[day];
                      if (modalElement && !modalElement.contains(e.relatedTarget as Node)) {
                        if (document.activeElement !== roleInputRef.current[day]) {
                          handleCloseRoleModal(day);
                          setEditingCell(null);
                        }
                      }
                    }, 200);
                  }}
                />
                {filteredRoles[day] && filteredRoles[day].length > 0 && (
                  <div
                    ref={(el) => {
                      if (el) roleModalRef.current[day] = el;
                    }}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginTop: "4px",
                      width: "200px",
                      backgroundColor: "#fff",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
                      zIndex: 1000,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(() => {
                      const currentRoleValue = getRoleForDay(day);
                      const sortedRoles = [...filteredRoles[day]].sort((a, b) => {
                        if (a === currentRoleValue) return -1;
                        if (b === currentRoleValue) return 1;
                        return 0;
                      });
                      return sortedRoles.map((role) => (
                        <div
                          key={role}
                          onClick={() => {
                            handleRoleChange(day, role);
                            handleCloseRoleModal(day);
                            setEditingCell(null);
                          }}
                          style={{
                            padding: "10px 12px",
                            fontSize: "11px",
                            cursor: "pointer",
                            borderBottom: "1px solid #F3F4F6",
                            backgroundColor: currentRoleValue === role ? "#EFF6FF" : "transparent",
                            color: currentRoleValue === role ? "#1E90FF" : "#1F2937",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (currentRoleValue !== role) {
                              e.currentTarget.style.backgroundColor = "#F3F4F6";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (currentRoleValue !== role) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          {role}
                        </div>
                      ));
                    })()}
                  </div>
                )}
                {filteredRoles[day] && filteredRoles[day].length === 0 && searchRole[day] && (
                  <div
                    ref={(el) => {
                      if (el) roleModalRef.current[day] = el;
                    }}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginTop: "4px",
                      width: "200px",
                      backgroundColor: "#fff",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
                      zIndex: 1000,
                      padding: "12px",
                      textAlign: "center",
                      fontSize: "12px",
                      color: "#9CA3AF",
                    }}
                  >
                    No se encontraron roles
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  cursor: "pointer",
                  padding: "4px 6px",
                  borderRadius: "4px",
                  fontSize: "11px",
                  color: currentRole === "DESCANSO" 
                    ? "#FFFFFF" // Texto blanco para día libre
                    : currentRole && currentRole !== "N/A" 
                      ? hasFreeDayConflict(day) 
                        ? "#F97316" // Texto naranja para conflicto (tiene puesto pero también día libre)
                        : "#1F2937" 
                      : "#9CA3AF",
                  backgroundColor: currentRole === "DESCANSO" 
                    ? "#F97316" // Fondo naranja para día libre
                    : hasFreeDayConflict(day) 
                      ? "#FFF4E6" // Fondo naranja claro para conflicto (tiene puesto pero también día libre)
                      : "transparent",
                  border: hasFreeDayConflict(day) && currentRole !== "DESCANSO" ? "2px solid #F97316" : "none",
                  transition: "all 0.2s",
                  textAlign: "center",
                  fontWeight: currentRole === "DESCANSO" || hasFreeDayConflict(day) ? "600" : "400",
                }}
                onMouseEnter={(e) => {
                  if (currentRole !== "DESCANSO") {
                    e.currentTarget.style.backgroundColor = hasFreeDayConflict(day) ? "#FFE8CC" : "#F3F4F6";
                    e.currentTarget.style.color = hasFreeDayConflict(day) ? "#F97316" : "#1E90FF";
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentRole !== "DESCANSO") {
                    e.currentTarget.style.backgroundColor = hasFreeDayConflict(day) ? "#FFF4E6" : "transparent";
                    e.currentTarget.style.color = currentRole && currentRole !== "N/A"
                      ? hasFreeDayConflict(day) ? "#F97316" : "#1F2937"
                      : "#9CA3AF";
                  }
                }}
                title={hasFreeDayConflict(day) ? "Puesto asignado en dia de descanso configurado" : currentRole === "DESCANSO" ? "Día libre configurado" : ""}
              >
                {currentRole === "N/A" ? "" : currentRole}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
});

export default ConfigHorarioSemanalRow;
